import { Paperclip } from "lucide-react";

import { formatFileSize } from "../../shared/feedback-schemas";
import {
  DISPLAYABLE_IMAGE_TYPES,
  type SupportAttachment,
  type SupportMessage,
  type SupportTicketDetail,
} from "../../shared/support-schemas";
import { Badge } from "../components/ui/badge";
import { cn } from "../lib/utils";
import { supportAttachmentUrl } from "./support-api";
import { CHANNEL_LABELS, formatTimestamp } from "./support-format";

/**
 * The conversation: customer messages, replies, and internal notes, oldest first. What changed on
 * the ticket is in its activity log beside the conversation.
 */
export function SupportThread({ ticket }: { ticket: SupportTicketDetail }) {
  return (
    <ol aria-label="Conversation" className="flex flex-col gap-3">
      {ticket.messages.map((message) => (
        <li key={message.id}>
          <MessageCard ticketId={ticket.id} message={message} />
        </li>
      ))}
    </ol>
  );
}

function messageLabel(message: SupportMessage): string {
  if (message.kind === "note") return "Internal note";
  if (message.kind === "reply") return "Reply";
  return `Customer · ${CHANNEL_LABELS[message.channel]}`;
}

function DeliveryBadge({ message }: { message: SupportMessage }) {
  if (message.kind !== "reply" || message.deliveryStatus === "sent") return null;
  if (message.deliveryStatus === "failed")
    return <Badge variant="destructive">Not delivered</Badge>;
  return <Badge variant="secondary">Not sent yet</Badge>;
}

function MessageCard({ ticketId, message }: { ticketId: string; message: SupportMessage }) {
  const note = message.kind === "note";
  return (
    <article
      aria-label={`${messageLabel(message)} from ${message.authorName}`}
      className={cn(
        "flex flex-col gap-2 rounded-xl px-5 py-4 shadow-card",
        note ? "bg-warning-background" : "bg-card",
      )}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{message.authorName}</span>
          <span className="text-sm text-muted-foreground">{messageLabel(message)}</span>
          {message.autoSubmitted ? <Badge variant="secondary">Automatic reply</Badge> : null}
          <DeliveryBadge message={message} />
        </span>
        <time className="text-sm text-muted-foreground">{formatTimestamp(message.createdAt)}</time>
      </header>
      <p className="whitespace-pre-wrap">{message.bodyText}</p>
      <MessageAttachments ticketId={ticketId} attachments={message.attachments} />
    </article>
  );
}

const isImage = (attachment: SupportAttachment) =>
  DISPLAYABLE_IMAGE_TYPES.has(attachment.mediaType.toLowerCase());

const attachmentLabel = (attachment: SupportAttachment) =>
  `${attachment.filename}, ${formatFileSize(attachment.byteSize)}`;

/** Images show in the message, like they do in an email; every attachment downloads when clicked. */
function MessageAttachments({
  ticketId,
  attachments,
}: {
  ticketId: string;
  attachments: readonly SupportAttachment[];
}) {
  if (attachments.length === 0) return null;
  const images = attachments.filter(isImage);
  const files = attachments.filter((attachment) => !isImage(attachment));
  return (
    <>
      {images.length > 0 ? (
        <ul aria-label="Images" className="flex flex-wrap gap-3">
          {images.map((attachment) => (
            <li key={attachment.id}>
              <a
                href={supportAttachmentUrl(ticketId, attachment.id)}
                download={attachment.filename}
                aria-label={attachmentLabel(attachment)}
                className="block overflow-hidden rounded-md border border-border hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <img
                  src={supportAttachmentUrl(ticketId, attachment.id)}
                  alt=""
                  loading="lazy"
                  className="block max-h-80 max-w-full object-contain"
                />
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {files.length > 0 ? (
        <ul aria-label="Attachments" className="flex flex-wrap gap-2">
          {files.map((attachment) => (
            <li key={attachment.id}>
              <a
                href={supportAttachmentUrl(ticketId, attachment.id)}
                download={attachment.filename}
                aria-label={attachmentLabel(attachment)}
                className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <Paperclip aria-hidden="true" className="size-3.5" />
                {attachment.filename}
                <span className="text-muted-foreground">{formatFileSize(attachment.byteSize)}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
